import React from 'react'
import cx from 'clsx'
import { NavLink, useLocation } from 'react-router-dom'
import { IconLock } from '@tabler/icons-react'

import routes from '@src/Routes/routes'
import Tooltip from '@components/Tooltip'

import styles from './index.module.css'
import { useTranslation } from 'react-i18next'
import { useHasPermission } from '@helpers/hooks/useHasPermission'
import useGetIsRolesTabEnabled from '@helpers/hooks/useGetIsRolesTabEnabled'
import { Perm } from '@type/permissions'

export interface HeaderProps {
  children?: React.ReactNode
}

export const Header: React.FC<HeaderProps> = ({ children }) => {
  const location = useLocation()
  const { t } = useTranslation('team')
  // Members + org-structure (Departments/Locations) tabs: team.view. The perm
  // is labelled "View team members and org structure" and the backend GET on
  // departments/locations is gated on team.view, so viewing them is a
  // team.view capability. Creating/editing/deleting stays gated on
  // manage_org_structure inside the pages (Add button + row Edit/Delete) and
  // on the backend writes. team.view is granted broadly (implied by
  // workflow.view), so those roles get read-only org-structure visibility —
  // benign, and useful for anyone configuring department-based approvers.
  const canViewMembers = useHasPermission(Perm.TEAM_VIEW)
  // Roles tab is visible to anyone who can view roles. With
  // manage-implies-view in place, any role that has role.manage also
  // gets role.view automatically.
  const canViewRoles = useHasPermission(Perm.ROLE_VIEW)
  // Beta gate: when the team hasn't had the Roles feature switched on in
  // Django admin, the tab stays visible (for role.view holders) but locked —
  // a lock icon + tooltip, and the page itself renders the teaser.
  const rolesTabEnabled = useGetIsRolesTabEnabled()
  // Roles renders on both web and the MS Teams embed (both route /roles to the
  // shared Team/Roles page under the shared Team layout).
  const showRolesTab = canViewRoles

  return (
    <div className="flex justify-between border-b flex-wrap-reverse gap-0.5 md:gap-2 mb-4 items-end">
      <nav className="flex">
        {canViewMembers && (
          <NavLink
            to={routes.private.organizationalStructure}
            className={({ isActive }) => {
              return cx(styles.tab, { [styles.active]: isActive && !location.pathname.includes('contract') })
            }}
          >
            {t('users.title')}
          </NavLink>
        )}
        {canViewMembers && (
          <>
            <NavLink
              to={routes.private.teamDepartments}
              className={({ isActive }) => {
                return cx(styles.tab, { [styles.active]: isActive && !location.pathname.includes('contract') })
              }}
            >
              {t('departments.title')}
            </NavLink>
            <NavLink
              to={routes.private.teamLocations}
              className={({ isActive }) => {
                return cx(styles.tab, { [styles.active]: isActive && !location.pathname.includes('contract') })
              }}
            >
              {t('locations.title')}
            </NavLink>
          </>
        )}
        {showRolesTab && (
          <NavLink
            to={routes.private.teamRoles}
            className={({ isActive }) => {
              return cx(styles.tab, { [styles.active]: isActive && !location.pathname.includes('contract') })
            }}
            data-tooltip-id={rolesTabEnabled ? undefined : 'roles-locked-tip'}
            data-tooltip-content={rolesTabEnabled ? undefined : t('roles.lockedTooltip')}
          >
            <span className="flex items-center gap-1">
              {t('roles.title')}
              {!rolesTabEnabled && (
                <IconLock size={14} className="text-gray-400" aria-label={t('roles.lockedTooltip')} />
              )}
            </span>
          </NavLink>
        )}
      </nav>
      {showRolesTab && !rolesTabEnabled && <Tooltip id="roles-locked-tip" />}
      {children}
    </div>
  )
}

export default Header
